
// Security monitoring system for OAuth flow
import { supabase } from '@/lib/supabase';

export interface SecurityEvent {
  type: 'oauth_failure' | 'rate_limit_hit' | 'csrf_mismatch' | 'suspicious_activity' | 'token_refresh_failure';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, any>;
  timestamp: number;
  userAgent?: string;
  ipAddress?: string;
}

export interface SecurityAlert {
  id: string;
  type: string;
  severity: 'medium' | 'high' | 'critical';
  message: string;
  count: number;
  firstOccurrence: number;
  lastOccurrence: number;
  resolved: boolean;
}

class SecurityMonitor {
  private alerts: Map<string, SecurityAlert> = new Map();
  private eventBuffer: SecurityEvent[] = [];
  private readonly maxBufferSize = 100;
  private readonly alertThresholds = {
    oauth_failure: { count: 5, timeWindow: 300000 }, // 5 failures in 5 minutes
    rate_limit_hit: { count: 3, timeWindow: 600000 }, // 3 hits in 10 minutes
    csrf_mismatch: { count: 2, timeWindow: 180000 }, // 2 mismatches in 3 minutes
    suspicious_activity: { count: 1, timeWindow: 60000 } // 1 in 1 minute
  };

  private isDevelopment = import.meta.env.DEV;

  // Log security events with structured data
  logSecurityEvent(event: Omit<SecurityEvent, 'timestamp' | 'userAgent'>): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      ipAddress: 'client-side' // Will be enriched server-side if needed
    };

    // Add to buffer
    this.eventBuffer.push(fullEvent);
    if (this.eventBuffer.length > this.maxBufferSize) {
      this.eventBuffer.shift();
    }

    // Development logging
    if (this.isDevelopment) {
      console.warn('🔒 Security Event:', {
        type: fullEvent.type,
        severity: fullEvent.severity,
        metadata: fullEvent.metadata
      });
    }

    // Check for patterns and generate alerts
    this.analyzeSecurityEvent(fullEvent);

    // Send to analytics service asynchronously
    this.sendToAnalytics(fullEvent);
  }

  // Analyze events for patterns and generate alerts
  private analyzeSecurityEvent(event: SecurityEvent): void {
    const threshold = this.alertThresholds[event.type];
    if (!threshold) return;

    const alertKey = `${event.type}_${event.userId || 'anonymous'}`;
    const now = Date.now();
    
    // Get recent events of same type for this user
    const recentEvents = this.eventBuffer.filter(e => 
      e.type === event.type &&
      e.userId === event.userId &&
      now - e.timestamp <= threshold.timeWindow
    );

    if (recentEvents.length >= threshold.count) {
      this.generateAlert({
        id: alertKey,
        type: event.type,
        severity: this.getSeverityFromEventType(event.type),
        message: this.getAlertMessage(event.type, recentEvents.length),
        count: recentEvents.length,
        firstOccurrence: recentEvents[0].timestamp,
        lastOccurrence: now,
        resolved: false
      });
    }
  }

  // Generate security alert
  private generateAlert(alert: SecurityAlert): void {
    this.alerts.set(alert.id, alert);

    if (this.isDevelopment) {
      console.error('🚨 Security Alert Generated:', alert);
    }

    // In production, this would trigger actual alerting (email, Slack, etc.)
    this.triggerAlertNotification(alert);
  }

  // Get severity based on event type
  private getSeverityFromEventType(type: SecurityEvent['type']): SecurityAlert['severity'] {
    switch (type) {
      case 'suspicious_activity':
      case 'csrf_mismatch':
        return 'critical';
      case 'oauth_failure':
      case 'token_refresh_failure':
        return 'high';
      case 'rate_limit_hit':
        return 'medium';
      default:
        return 'medium';
    }
  }

  // Get human-readable alert message
  private getAlertMessage(type: SecurityEvent['type'], count: number): string {
    switch (type) {
      case 'oauth_failure':
        return `${count} OAuth failures detected - possible attack or system issue`;
      case 'rate_limit_hit':
        return `${count} rate limit violations - possible abuse attempt`;
      case 'csrf_mismatch':
        return `${count} CSRF token mismatches - possible CSRF attack`;
      case 'suspicious_activity':
        return `Suspicious activity detected - immediate investigation required`;
      case 'token_refresh_failure':
        return `${count} token refresh failures - possible token compromise`;
      default:
        return `${count} security events of type ${type} detected`;
    }
  }

  // Trigger alert notification (placeholder for real implementation)
  private triggerAlertNotification(alert: SecurityAlert): void {
    // In production, implement actual alerting:
    // - Email notifications
    // - Slack/Discord webhooks  
    // - SMS for critical alerts
    // - Dashboard notifications
    
    if (alert.severity === 'critical') {
      // Critical alerts would trigger immediate notifications
      console.error('🚨 CRITICAL SECURITY ALERT:', alert.message);
    }
  }

  // Send events to analytics service
  private async sendToAnalytics(event: SecurityEvent): Promise<void> {
    try {
      // Only send to database in production or when explicitly enabled
      if (!this.isDevelopment && supabase) {
        await supabase.from('security_events').insert({
          event_type: event.type,
          severity: event.severity,
          user_id: event.userId,
          session_id: event.sessionId,
          metadata: event.metadata,
          timestamp: new Date(event.timestamp).toISOString(),
          user_agent: event.userAgent
        });
      }
    } catch (error) {
      // Fail silently to not impact user experience
      if (this.isDevelopment) {
        console.error('Failed to send security event to analytics:', error);
      }
    }
  }

  // Get current alerts
  getActiveAlerts(): SecurityAlert[] {
    return Array.from(this.alerts.values()).filter(alert => !alert.resolved);
  }

  // Resolve an alert
  resolveAlert(alertId: string): void {
    const alert = this.alerts.get(alertId);
    if (alert) {
      alert.resolved = true;
      this.alerts.set(alertId, alert);
    }
  }

  // Get security event statistics
  getSecurityStats(timeWindow: number = 3600000): {
    totalEvents: number;
    eventsByType: Record<string, number>;
    eventsBySeverity: Record<string, number>;
    activeAlerts: number;
  } {
    const now = Date.now();
    const recentEvents = this.eventBuffer.filter(e => now - e.timestamp <= timeWindow);
    
    const eventsByType: Record<string, number> = {};
    const eventsBySeverity: Record<string, number> = {};
    
    recentEvents.forEach(event => {
      eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
      eventsBySeverity[event.severity] = (eventsBySeverity[event.severity] || 0) + 1;
    });

    return {
      totalEvents: recentEvents.length,
      eventsByType,
      eventsBySeverity,
      activeAlerts: this.getActiveAlerts().length
    };
  }

  // Clear old events from buffer
  cleanup(): void {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    this.eventBuffer = this.eventBuffer.filter(event => 
      now - event.timestamp <= maxAge
    );

    // Clean up resolved alerts older than 7 days
    const weekAgo = now - (7 * 24 * 60 * 60 * 1000);
    for (const [id, alert] of this.alerts.entries()) {
      if (alert.resolved && alert.lastOccurrence < weekAgo) {
        this.alerts.delete(id);
      }
    }
  }
}

// Export singleton instance
export const securityMonitor = new SecurityMonitor();

// Utility functions for common security events
export const logOAuthFailure = (userId: string, error: string, metadata?: Record<string, any>) => {
  securityMonitor.logSecurityEvent({
    type: 'oauth_failure',
    severity: 'high',
    userId,
    metadata: { error, ...metadata }
  });
};

export const logRateLimitHit = (userId: string, endpoint: string) => {
  securityMonitor.logSecurityEvent({
    type: 'rate_limit_hit',
    severity: 'medium',
    userId,
    metadata: { endpoint }
  });
};

export const logCSRFMismatch = (userId: string, expectedToken: string, receivedToken: string) => {
  securityMonitor.logSecurityEvent({
    type: 'csrf_mismatch',
    severity: 'critical',
    userId,
    metadata: { 
      expectedTokenPrefix: expectedToken?.substring(0, 8),
      receivedTokenPrefix: receivedToken?.substring(0, 8)
    }
  });
};

export const logSuspiciousActivity = (userId: string, activity: string, metadata?: Record<string, any>) => {
  securityMonitor.logSecurityEvent({
    type: 'suspicious_activity',
    severity: 'critical',
    userId,
    metadata: { activity, ...metadata }
  });
};

export const logTokenRefreshFailure = (userId: string, error: string) => {
  securityMonitor.logSecurityEvent({
    type: 'token_refresh_failure',
    severity: 'high',
    userId,
    metadata: { error }
  });
};

// Initialize cleanup interval
if (typeof window !== 'undefined') {
  setInterval(() => {
    securityMonitor.cleanup();
  }, 60 * 60 * 1000); // Run cleanup every hour
}
